import { useState, useEffect } from 'react';
import StockTable from './StockTable';
import ThemeGroup from './ThemeGroup';
import NewsPanel from './NewsPanel';
import MarketNews from './MarketNews';
import useWebSocket from '../hooks/useWebSocket';
import { DisguiseProvider, useDisguise } from '../context/DisguiseContext';
import useAuth from '../hooks/useAuth';
import { apiGet } from '../utils/api';
import { getToken } from '../hooks/useAuth';

// WebSocket URL 자동 결정
//  - 환경변수 있으면 사용 (로컬: ws://223.130.157.202:8000)
//  - 없으면 현재 도메인의 같은 프로토콜 사용
//    https://stocktool.co.kr → wss://stocktool.co.kr
//    http://localhost:3000 → ws://localhost:3000 (사실상 안 씀)
const WS = process.env.REACT_APP_WS_URL
        || ((typeof window !== 'undefined')
            ? (window.location.protocol === 'https:' ? 'wss://' : 'ws://') + window.location.host
            : '');


// Dashboard 본체 - DisguiseProvider 안에서 동작
function DashboardInner() {
  const { disguised, excel, mode, setMode } = useDisguise();
  const { user, logout } = useAuth();

  const [themes, setThemes]             = useState([]);
  const [selectedCode, setSelectedCode] = useState(null);
  const [selectedName, setSelectedName] = useState('');
  const [lastUpdated, setLastUpdated]   = useState(null);
  const [isMobile, setIsMobile]         = useState(
    typeof window !== 'undefined' && window.innerWidth < 600
  );

  // WebSocket URL 고정 (최초 1회만 연결 — 재연결 없음)
  // codes 변경은 updateCodes()로 기존 연결에 메시지 전송
  const wsUrl = `${WS}/ws/realtime?token=${encodeURIComponent(getToken())}`;
  const { tickData, updateCodes } = useWebSocket(wsUrl);

  // 모바일 감지 (600px 미만)
  useEffect(() => {
    function onResize() {
      setIsMobile(window.innerWidth < 600);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 테마 갱신
  useEffect(() => {
    fetchThemes();
    const interval = setInterval(fetchThemes, 1 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  async function fetchThemes() {
    try {
      const d = await apiGet('/api/theme/top?top_n=15');
      setThemes(Array.isArray(d) ? d : []);
      setLastUpdated(new Date().toLocaleTimeString('ko-KR'));
    } catch (e) {
      console.error('테마 오류:', e);
    }
  }

  function handleSelect(code, name) {
    setSelectedCode(code);
    setSelectedName(name);
  }

  function handleStocksLoaded(codes) {
    // WebSocket 재연결 없이 구독 종목만 교체
    updateCodes(codes.slice(0, 30));
  }

  // 모드별 표시 데이터
  const title = excel   ? '📋 분기별 데이터 현황.xlsx'
              : disguised ? '📊 Q3 Data Analysis Dashboard'
              : '📊 StockTool 주식도구';
  const updatedLabel  = (excel || disguised) ? 'Updated' : '갱신';
  const loadingLabel  = (excel || disguised) ? 'Loading...' : '로딩 중...';
  const selectedLabel = excel ? 'Cell' : disguised ? 'Selected' : '선택';
  const selectedDisplay = excel && selectedCode
    ? `Row-${String(selectedCode).slice(-3)}`
    : disguised && selectedCode
    ? `ID-${selectedCode.slice(-3)}`
    : selectedCode ? `${selectedName} (${selectedCode})` : '';

  return (
    <div style={{
      ...styles.container,
      background: excel ? '#ffffff' : '#0d1117',
      color:      excel ? '#000000' : '#e6edf3',
    }}>
      <header style={{
        ...styles.header,
        ...(isMobile ? {
          display: 'flex',
          gridTemplateColumns: 'none',
          justifyContent: 'space-between',
          gap: 8,
        } : {}),
        background:   excel ? '#f2f2f2' : '#161b22',
        borderBottom: excel ? '2px solid #d0d0d0' : '1px solid #30363d',
      }}>
        <div style={styles.headerLeft}>
          {(!excel && !disguised) ? (
            <span style={{
              ...styles.title, color: '#e6edf3',
              display: 'inline-flex', alignItems: 'center', gap: 8,
            }}>
              <svg width="26" height="26" viewBox="0 0 34 34" style={{ flexShrink: 0 }}>
                <rect width="34" height="34" rx="9" fill="#388bfd" />
                <line x1="12" y1="8" x2="12" y2="26" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                <line x1="22" y1="8" x2="22" y2="26" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                <rect x="9.5" y="13" width="5" height="8" rx="1.5" fill="#fff" />
                <rect x="19.5" y="10" width="5" height="9" rx="1.5" fill="#fff" />
              </svg>
              <span>StockTool{' '}
                <span style={{ color: '#8b949e', fontWeight: 400, fontSize: '0.82em' }}>주식도구</span>
              </span>
            </span>
          ) : (
            <span style={{
              ...styles.title,
              color:      excel ? '#000' : '#e6edf3',
              fontFamily: excel ? "'Calibri', 'Arial', sans-serif" : 'inherit',
            }}>{title}</span>
          )}
          <span style={{ ...styles.updated, color: excel ? '#666' : '#8b949e' }}>
            {lastUpdated ? `${updatedLabel}: ${lastUpdated}` : loadingLabel}
          </span>
          {selectedCode && (
            <span style={{ ...styles.selected, color: excel ? '#1a6b1a' : '#58a6ff' }}>
              {selectedLabel}: {selectedDisplay}
            </span>
          )}
        </div>
        <div style={styles.headerCenter}>
          {/* 모드 버튼 3개 */}
          <div style={{ display: 'flex', gap: 4 }}>
            {[
              { key: 'normal',  label: '일반' },
              { key: 'special', label: '⚙ 특수' },
              { key: 'excel',   label: '📊 엑셀' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setMode(key)}
                title={key === 'excel' ? 'Ctrl+Shift+B' : 'Ctrl+B'}
                style={{
                  fontSize: 11, padding: '4px 10px', borderRadius: 4,
                  cursor: 'pointer', whiteSpace: 'nowrap',
                  border: excel ? '1px solid #d0d0d0' : '1px solid #30363d',
                  background: mode === key
                    ? (excel ? '#217346' : '#388bfd')
                    : (excel ? '#ffffff'  : '#21262d'),
                  color: mode === key
                    ? '#ffffff'
                    : (excel ? '#333' : '#8b949e'),
                }}
              >{label}</button>
            ))}
          </div>
        </div>
        <div style={{ ...styles.headerRight, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
          {user && !excel && (
            <span style={{ fontSize: 11, color: '#8b949e' }}>{user.email}</span>
          )}
          <button onClick={logout} title="로그아웃" style={{
            fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
            border: excel ? '1px solid #d0d0d0' : '1px solid #30363d',
            background: excel ? '#fff' : '#21262d',
            color: excel ? '#333' : '#8b949e',
          }}>로그아웃</button>
        </div>
      </header>

      {/* 본문 그리드 - PC: 3분할 / 모바일: 2분할 (시세 + 뉴스) */}
      <div style={{
        ...styles.grid,
        gridTemplateColumns: isMobile ? '1.1fr 1fr' : '1fr 0.8fr 1.4fr',
        background: excel ? '#d0d0d0' : '#30363d',
      }}>
        <div style={{ ...styles.panel, background: excel ? '#ffffff' : '#0d1117' }}>
          <StockTable
            onSelect={handleSelect}
            selected={selectedCode}
            tickData={tickData}
            onStocksLoaded={handleStocksLoaded}
          />
        </div>
        {!isMobile && (
          <div style={{
            ...styles.panel,
            background: excel ? '#ffffff' : '#0d1117',
            opacity: mode === 'special' ? 0.35 : 1,
            transition: 'opacity 0.2s',
            display: 'flex', flexDirection: 'column', gap: 10,
          }}>
            <div style={{ flex: '1 1 52%', minHeight: 0 }}>
              <ThemeGroup themes={themes} />
            </div>
            <div style={{ flex: '1 1 48%', minHeight: 0 }}>
              <MarketNews />
            </div>
          </div>
        )}
        <div style={{ ...styles.panel, background: excel ? '#ffffff' : '#0d1117' }}>
          <NewsPanel
            selectedCode={selectedCode}
            selectedName={selectedName}
          />
        </div>
      </div>
    </div>
  );
}

// 외부 노출 컴포넌트 - DisguiseProvider로 감싼 Dashboard
export default function Dashboard() {
  return (
    <DisguiseProvider>
      <DashboardInner />
    </DisguiseProvider>
  );
}

const styles = {
  container: {
    display: 'flex', flexDirection: 'column',
    height: '100vh', background: '#0d1117', color: '#e6edf3',
  },
  header: {
    // 본문 그리드와 동일 비율 (1fr 0.8fr 1.4fr) 적용 → 버튼이 테마 컬럼 중앙에 위치
    display: 'grid',
    gridTemplateColumns: '1fr 0.8fr 1.4fr',
    alignItems: 'center',
    padding: '8px 16px', background: '#161b22',
    borderBottom: '1px solid #30363d', flexShrink: 0,
    gap: 1,
  },
  headerLeft: {
    display: 'flex', alignItems: 'center', gap: 16,
    minWidth: 0,           // grid 아이템 overflow 방지
    overflow: 'hidden',    // 긴 ETF 종목명이 옆 컬럼 침범 방지
  },
  headerCenter: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'center',   // 테마 컬럼 정중앙 배치
  },
  headerRight: {},
  title:    { fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap' },
  updated:  { fontSize: 12, color: '#8b949e', whiteSpace: 'nowrap' },
  selected: {
    fontSize: 12, color: '#58a6ff', whiteSpace: 'nowrap',
    overflow: 'hidden', textOverflow: 'ellipsis',
    minWidth: 0,           // ellipsis 동작에 필수
  },
  disguiseBtn: {
    fontSize: 11, padding: '4px 10px', borderRadius: 4,
    cursor: 'pointer', border: '1px solid #30363d',
    whiteSpace: 'nowrap',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 0.8fr 1.4fr',
    flex: 1,
    gap: 1,
    background: '#30363d',
    overflow: 'hidden',
  },
  panel: {
    background: '#0d1117',
    overflow: 'hidden',
    padding: 12,
  },
};
